import { useCallback, useEffect, useRef } from 'react';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import {
  Bold,
  Code,
  Heading2,
  Heading3,
  ImagePlus,
  Italic,
  Link2,
  List,
  ListOrdered,
  Quote,
  Redo2,
  Strikethrough,
  Undo2,
} from 'lucide-react';
import { cn } from '@/lib/cn';

type Props = {
  value: string;
  onChange: (html: string) => void;
  /** Uploads a file and resolves to its public URL for inline insertion. */
  onUploadImage?: (file: File) => Promise<string>;
  placeholder?: string;
  disabled?: boolean;
};

/**
 * Tiptap-based rich text editor. Emits clean HTML that maps 1:1 to the server's
 * BlogContentSanitizer allowlist (p, headings, lists, blockquote, code, links,
 * images). Whatever slips past is stripped on save, so the editor never has to
 * be the security boundary — it just needs to feel good.
 */
export function RichTextEditor({ value, onChange, onUploadImage, placeholder, disabled }: Props) {
  const fileInput = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    editable: !disabled,
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3, 4] },
        link: {
          openOnClick: false,
          autolink: true,
          HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
        },
      }),
      Image.configure({ inline: false, HTMLAttributes: { class: 'rounded-lg' } }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: 'min-h-[320px] px-4 py-3 focus:outline-none',
        'data-placeholder': placeholder ?? 'Write your post…',
      },
    },
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // Keep the editor in sync when the post loads or is swapped underneath us,
  // without clobbering the cursor on every local keystroke.
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [disabled, editor]);

  const pickImage = useCallback(() => fileInput.current?.click(), []);

  const onFileChosen = useCallback(
    async (file: File | undefined) => {
      if (!file || !editor || !onUploadImage) return;
      const url = await onUploadImage(file);
      editor.chain().focus().setImage({ src: url }).run();
    },
    [editor, onUploadImage],
  );

  const setLink = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL', previous ?? 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] focus-within:border-[var(--color-ring)]">
      <Toolbar>
        <ToolButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} label="Bold">
          <Bold className="size-4" />
        </ToolButton>
        <ToolButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} label="Italic">
          <Italic className="size-4" />
        </ToolButton>
        <ToolButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} label="Strikethrough">
          <Strikethrough className="size-4" />
        </ToolButton>
        <Divider />
        <ToolButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} label="Heading 2">
          <Heading2 className="size-4" />
        </ToolButton>
        <ToolButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} label="Heading 3">
          <Heading3 className="size-4" />
        </ToolButton>
        <Divider />
        <ToolButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} label="Bullet list">
          <List className="size-4" />
        </ToolButton>
        <ToolButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} label="Numbered list">
          <ListOrdered className="size-4" />
        </ToolButton>
        <ToolButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} label="Quote">
          <Quote className="size-4" />
        </ToolButton>
        <ToolButton onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} label="Code block">
          <Code className="size-4" />
        </ToolButton>
        <Divider />
        <ToolButton onClick={setLink} active={editor.isActive('link')} label="Link">
          <Link2 className="size-4" />
        </ToolButton>
        {onUploadImage ? (
          <ToolButton onClick={pickImage} label="Insert image">
            <ImagePlus className="size-4" />
          </ToolButton>
        ) : null}
        <div className="ml-auto flex items-center gap-0.5">
          <ToolButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} label="Undo">
            <Undo2 className="size-4" />
          </ToolButton>
          <ToolButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} label="Redo">
            <Redo2 className="size-4" />
          </ToolButton>
        </div>
      </Toolbar>
      <EditorContent editor={editor} />
      <input
        ref={fileInput}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
        className="hidden"
        onChange={(e) => {
          void onFileChosen(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </div>
  );
}

function Toolbar({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-[var(--color-border)] bg-[var(--color-surface-muted)] px-1.5 py-1">
      {children}
    </div>
  );
}

function Divider() {
  return <span aria-hidden className="mx-1 h-5 w-px bg-[var(--color-border)]" />;
}

function ToolButton({
  onClick,
  active,
  disabled,
  label,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-pressed={active}
      title={label}
      className={cn(
        'inline-flex size-8 items-center justify-center rounded transition-colors',
        'text-[var(--color-foreground-muted)] hover:bg-[var(--color-surface-elevated)] hover:text-[var(--color-foreground)]',
        'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent',
        active && 'bg-[var(--color-surface-elevated)] text-[var(--color-foreground)]',
      )}
    >
      {children}
    </button>
  );
}

export type { Editor };
